/**
 * Qué tarjetas siguen esperando una decisión (§8.2).
 *
 * Importa más de lo que parece: mientras una confirmación o una pregunta no se
 * contestan, el bucle del agente está PARADO. Si la ventana del chat está
 * cerrada y nadie avisa, el agente espera a alguien que no sabe que le
 * preguntaron.
 *
 * Hacen falta las dos fuentes y no basta ninguna sola:
 *
 * - `settled` lo dice el BACKEND al releer la conversación, y cubre lo que
 *   pasó antes de recargar la página --incluida la caducidad--;
 * - `answered` lo lleva ESTA sesión, y cubre lo que el usuario acaba de
 *   decidir sin haber recargado nada.
 *
 * Es una función pura y vive fuera del hook porque es la regla, no el estado:
 * así se puede probar sin montar un chat entero.
 */
import type {
  AgentMessage,
  AgentPart,
  ConfirmationResult,
} from "./agent-event.ts";
import { CHAT_TEXTS as TEXTS } from "./texts.ts";

/** El id con el que se responde a una tarjeta, si la parte es una. */
export const decisionId = (part: AgentPart): string | null => {
  if (part.type === "confirmation_request") return part.actionId;
  if (part.type === "question_request") return part.questionId;
  return null;
};

const isWaiting = (part: AgentPart, answered: ReadonlySet<string>): boolean => {
  const id = decisionId(part);
  if (id === null) return false;

  const settled =
    (part.type === "confirmation_request" ||
      part.type === "question_request") &&
    part.settled === true;

  return !settled && !answered.has(id);
};

export const countPendingDecisions = (
  messages: readonly AgentMessage[],
  answered: ReadonlySet<string>,
): number =>
  messages.reduce(
    (total, message) =>
      total + message.parts.filter((part) => isWaiting(part, answered)).length,
    0,
  );

/**
 * ¿Es esta salida el sobre de "espera tu confirmación" y no un resultado?
 *
 * Cuando el modelo pide una escritura, la herramienta NO se ejecuta: el bucle
 * le devuelve al modelo un sobre `{ estado: "pendiente_de_confirmacion" }` y
 * pone una tarjeta. Es la única señal que trae la llamada, porque la tarjeta
 * no dice a qué llamadas pertenece (no lleva `callId`). Se lee aquí y en
 * ningún otro sitio; lo que se mira es una constante del backend, no su
 * prosa.
 */
const AWAITING_CONFIRMATION = "pendiente_de_confirmacion";

export const isAwaitingConfirmation = (output: unknown): boolean =>
  typeof output === "object" &&
  output !== null &&
  (output as { estado?: unknown }).estado === AWAITING_CONFIRMATION;

/**
 * Lo que se sabe de las decisiones de la conversación abierta.
 *
 * - `answered`: qué tarjetas se contestaron en esta sesión.
 * - `decisions`: cómo se decidió cada una en esta sesión (`true`: aprobar).
 *   Cubre el instante entre el clic y que llegue el resultado.
 * - `outcomes`: cómo acabó cada tarjeta según el backend (R2.1), por el
 *   `actionId` de la tarjeta. Es lo que manda en cuanto está.
 */
export interface DecisionState {
  readonly answered: ReadonlySet<string>;
  readonly decisions: ReadonlyMap<string, boolean>;
  readonly outcomes: ReadonlyMap<string, ConfirmationResult>;
}

/** Los resultados de todas las tarjetas de la conversación, por tarjeta. */
export const collectOutcomes = (
  messages: readonly AgentMessage[],
): ReadonlyMap<string, ConfirmationResult> =>
  new Map(
    messages.flatMap((message) =>
      (message.confirmationResults ?? []).map(
        (result) => [result.actionId, result] as const,
      ),
    ),
  );

type Card = Extract<AgentPart, { type: "confirmation_request" }>;
type ActionResult = ConfirmationResult["results"][number];

/**
 * Cómo está una tarjeta.
 *
 * - `waiting`: nadie la ha contestado.
 * - `deciding`: se acaba de decidir en esta sesión y el resultado aún no ha
 *   llegado.
 * - `approved`, con lo que pasó en cada acción; `rejected`; `expired`.
 * - `resolved`: ya no espera, pero no consta cómo acabó --una conversación de
 *   antes de que el backend lo contara--. Se dice eso y nada más: inventarse
 *   un "aprobada" sería peor que no saberlo.
 */
export type CardOutcome =
  | { readonly kind: "waiting" }
  | { readonly kind: "deciding"; readonly approved: boolean }
  | { readonly kind: "approved"; readonly results: readonly ActionResult[] }
  | { readonly kind: "rejected" }
  | { readonly kind: "expired" }
  | { readonly kind: "resolved" };

export const cardOutcome = (card: Card, state: DecisionState): CardOutcome => {
  const result = state.outcomes.get(card.actionId);
  if (result !== undefined) {
    return result.outcome === "approved"
      ? { kind: "approved", results: result.results }
      : { kind: result.outcome };
  }

  if (isWaiting(card, state.answered)) return { kind: "waiting" };

  const decided = state.decisions.get(card.actionId);
  return decided === undefined
    ? { kind: "resolved" }
    : { kind: "deciding", approved: decided };
};

/**
 * La cabecera de una tarjeta aprobada: cuántas se hicieron y cuántas fallaron.
 *
 * Lo que falla va SIEMPRE en la cabecera, aunque sea una de ocho: es justo lo
 * que hay que ver, y esconderlo bajo un "aprobada" era el defecto (R2.1).
 */
export const summarizeResults = (
  results: readonly ActionResult[],
): { readonly text: string; readonly failed: number } => {
  const failed = results.filter((result) => result.status === "failed").length;
  const done = results.length - failed;

  if (results.length === 1) {
    return {
      text: failed === 0 ? TEXTS.results.done : TEXTS.results.failed,
      failed,
    };
  }

  // "1 de 2 hechas" y no "hecha": concuerda con el total, como "7 de 8".
  const head = TEXTS.results.someDone(done, results.length);
  if (failed === 0) return { text: head, failed };
  return {
    text: TEXTS.results.withFailures(head, failed),
    failed,
  };
};

/** El resultado de una acción de la tarjeta, si ya se sabe. */
export const actionResult = (
  outcome: CardOutcome,
  actionId: string,
): ActionResult | undefined =>
  outcome.kind === "approved"
    ? outcome.results.find((result) => result.actionId === actionId)
    : undefined;

/**
 * En qué quedó una llamada que esperaba confirmación.
 *
 * `failed` trae el motivo. `approved` es "se aprobó y aún no consta cómo
 * salió".
 */
export type CallResolution =
  | { readonly kind: "waiting" }
  | { readonly kind: "approved" }
  | { readonly kind: "done" }
  | { readonly kind: "failed"; readonly error: string | null }
  | { readonly kind: "rejected" }
  | { readonly kind: "expired" }
  | { readonly kind: "resolved" };

/** Qué tarjeta del mensaje pidió esta llamada, por su `callId` (R2.1). */
const cardOfCall = (message: AgentMessage, callId: string): Card | undefined =>
  message.parts.find(
    (part): part is Card =>
      part.type === "confirmation_request" &&
      (part.callId === callId ||
        (part.items?.some((item) => item.callId === callId) ?? false)),
  );

const fromCard = (outcome: CardOutcome, callId: string): CallResolution => {
  switch (outcome.kind) {
    case "waiting":
    case "rejected":
    case "expired":
    case "resolved":
      return { kind: outcome.kind };
    case "deciding":
      return { kind: outcome.approved ? "approved" : "rejected" };
    case "approved": {
      const result = outcome.results.find((item) => item.callId === callId);
      if (result === undefined) return { kind: "approved" };
      return result.status === "done"
        ? { kind: "done" }
        : { kind: "failed", error: result.error };
    }
  }
};

/**
 * La resolución de una llamada que esperaba confirmación.
 *
 * Desde R2.1 la tarjeta dice qué llamadas son suyas (`callId`), y el
 * resultado, cómo salió cada una. En conversaciones de antes no hay
 * `callId`: entonces se decide por MENSAJE --el turno se para en cuanto hay
 * una tarjeta (§8.2), así que las llamadas pendientes de un mensaje son las
 * de sus tarjetas-- y solo se da un veredicto si todas acabaron igual.
 */
export const callResolution = (
  message: AgentMessage,
  callId: string,
  state: DecisionState,
): CallResolution => {
  const card = cardOfCall(message, callId);
  if (card !== undefined) return fromCard(cardOutcome(card, state), callId);

  const cards = message.parts.filter(
    (part): part is Card => part.type === "confirmation_request",
  );
  const outcomes = cards.map((item) => cardOutcome(item, state));
  if (outcomes.some((outcome) => outcome.kind === "waiting")) {
    return { kind: "waiting" };
  }

  // Sin `callId` no se sabe qué acción era esta llamada, así que tampoco si
  // salió bien: como mucho, si se aprobó, se rechazó o caducó todo.
  const verdicts = new Set(
    outcomes.map((outcome) =>
      outcome.kind === "deciding"
        ? outcome.approved
          ? "approved"
          : "rejected"
        : outcome.kind,
    ),
  );
  const [only] = verdicts;
  if (
    verdicts.size === 1 &&
    (only === "approved" || only === "rejected" || only === "expired")
  ) {
    return { kind: only };
  }
  return { kind: "resolved" };
};
